/**
 * Reads the contest, the fan's entries, the round list and the selected
 * round's board, resolves the selected entry and round, and returns the
 * view phase (S1, S5). Mirrors `PickSheetContainer.tsx`'s contest, entry and
 * board reads, but for the round `?roundId=` picks rather than always the
 * contest's current round: the selected round is resolved through
 * `resolveSelectedRoundId`, not read straight off `contestData`.
 */
import {
  useContestGet,
  useContestList,
  useEntryListMine,
  useRoundGetBoard,
  useRoundList,
} from "@sportsmet/contest-sdk/private";
import { useMemo } from "react";

import {
  formatContestStartLabel,
  resolvePickBoardPhase,
  type typeBoardData,
  type typeContestData,
  type typeEntryStatus,
  type typePickBoardPhase,
} from "../pick/pickBoardModel";
import { pickUpcomingContestNote, pickWaitingNote } from "../pick/pickCopy";
import type { typeStandingsContestOption } from "../poolStandings/poolStandingsModel";
import { type typeTeamName, useTeamNames } from "../pick/useTeamNames";
import {
  survivorLookBackNoEntryNote,
  survivorLookBackNote,
} from "./survivorGameCopy";
import {
  buildGameContestOptions,
  buildRoundChips,
  resolveLookBackContest,
  resolveSelectedRoundId,
  type typeRoundChip,
} from "./survivorGameModel";

export type typeSurvivorGameView =
  { kind: "loading" } | { kind: "failed" } | typePickBoardPhase;

export type typeSurvivorEntryOption = {
  entryId: string;
  label: string;
  status: typeEntryStatus;
};

export type typeSurvivorGameData = {
  contestId: string;
  /**
   * ISSUE-1331. The contest menu's options: the viewer's contest plus every
   * earlier contest of the season. Empty hides the menu.
   */
  contestOptions: readonly typeStandingsContestOption[];
  /** The menu's value, undefined when the viewer has no contest of their own. */
  contestMenuValue: string | undefined;
  /** True while the tab shows an earlier contest read only. */
  isLookingBack: boolean;
  contestData: typeContestData | null;
  /**
   * Whether the Add another entry button shows. Resolved by contest-api on
   * `/entry/list-mine` (ISSUE-1043); this app renders it and derives nothing.
   */
  canAddEntry: boolean;
  entryOptions: readonly typeSurvivorEntryOption[];
  selectedEntryId: string;
  entryStatus: typeEntryStatus | null;
  hasEntry: boolean;
  roundChips: typeRoundChip[];
  selectedRoundId: string;
  boardData: typeBoardData | null;
  view: typeSurvivorGameView;
  /**
   * True when the pool's current season holds an upcoming, active or
   * pendingOutcome contest (ISSUE-1042).
   */
  hasScheduledContest: boolean;
  teamsById: Map<string, typeTeamName>;
  refetchBoard: () => void;
  retry: () => void;
};

export function useSurvivorGameData(input: {
  poolId: string;
  requestedRoundId: string | undefined;
  requestedEntryId: string | undefined;
  /** `?contestId=`, shared with the Results tab's contest menu (ISSUE-1331). */
  requestedContestId?: string | undefined;
}): typeSurvivorGameData {
  const contestList = useContestList({ poolId: input.poolId });
  const listData = contestList.data?.data ?? null;
  const contests = listData?.items ?? [];
  const viewerContestId = listData?.viewerContestId ?? null;
  const lookBack = resolveLookBackContest({
    requestedContestId: input.requestedContestId,
    viewerContestId,
    items: contests,
  });
  const contestOptions = buildGameContestOptions({
    items: contests,
    viewerContestId,
  });
  const contestId = lookBack?.contestId ?? viewerContestId ?? "";
  const viewerContestState = listData?.viewerContestState ?? "none";
  const viewerContest =
    contests.find((item) => item.contestId === viewerContestId) ?? null;
  const waitingNote =
    viewerContestState === "waiting" && viewerContest !== null
      ? pickWaitingNote({
          contestName: viewerContest.name,
          startDateLabel: formatContestStartLabel(viewerContest.startDate),
        })
      : null;
  const upcomingNote =
    viewerContestState === "spectating" &&
    viewerContest !== null &&
    viewerContest.status === "upcoming"
      ? pickUpcomingContestNote({
          contestName: viewerContest.name,
          startDateLabel: formatContestStartLabel(viewerContest.startDate),
        })
      : null;
  const hasScheduledContest = contests.some(
    (item) =>
      item.status === "upcoming" ||
      item.status === "active" ||
      item.status === "pendingOutcome",
  );

  const contest = useContestGet(
    { poolId: input.poolId, contestId },
    { enabled: contestId !== "" },
  );
  const contestData = contest.data?.data ?? null;

  const entries = useEntryListMine(
    { poolId: input.poolId, contestId },
    { enabled: contestId !== "" },
  );
  const entryItems = entries.data?.data?.items ?? [];
  // An earlier contest never takes a new entry, whatever the server says.
  const canAddEntry =
    lookBack === null && entries.data?.data?.canAddEntry === true;

  const selectedEntryId =
    (input.requestedEntryId &&
    entryItems.some((item) => item.entryId === input.requestedEntryId)
      ? input.requestedEntryId
      : entryItems[0]?.entryId) ?? "";

  const entryOptions = useMemo(
    () =>
      entryItems.map((item) => ({
        entryId: item.entryId,
        label: item.label,
        status: item.status,
      })),
    [entryItems],
  );
  const entryStatus =
    entryItems.find((item) => item.entryId === selectedEntryId)?.status ?? null;
  const hasEntry = entryItems.length > 0;

  const roundList = useRoundList(
    { poolId: input.poolId, contestId, limit: 500 },
    { enabled: contestId !== "" },
  );
  const rounds = roundList.data?.data?.items ?? [];
  const selectedRoundId = resolveSelectedRoundId({
    requestedRoundId: input.requestedRoundId,
    currentRoundId: contestData?.currentRoundId,
    rounds,
  });
  const roundChips = useMemo(
    () =>
      buildRoundChips({
        rounds,
        grouping: contestData?.config.roundGrouping ?? "week",
      }),
    [rounds, contestData],
  );

  const board = useRoundGetBoard(
    {
      poolId: input.poolId,
      contestId,
      roundId: selectedRoundId,
      entryId: selectedEntryId,
    },
    {
      enabled:
        contestId !== "" && selectedRoundId !== "" && selectedEntryId !== "",
    },
  );
  const boardData = board.data?.data ?? null;

  const teamNames = useTeamNames({ poolId: input.poolId });

  const phase = resolvePickBoardPhase({
    contestStatus: contestData?.status ?? null,
    currentRoundId: selectedRoundId,
    entryStatus,
    hasEntry,
    board: boardData,
    viewerContestState,
    waitingNote,
  });
  // ISSUE-1042 R3: an upcoming contest the viewer holds no entry in still
  // resolves to noContest, because upcoming is not playable. The kind stays
  // noContest so the status bar and every other branch behave as before; only
  // the line names the contest and its start day.
  // ISSUE-1331. Looking back at an earlier contest wins over every play
  // phase: its rounds are locked and its status may be final, which the play
  // phases would read as "no contest".
  const shownPhase: typePickBoardPhase =
    lookBack !== null
      ? {
          kind: "lookBack",
          note: hasEntry
            ? survivorLookBackNote(lookBack.name)
            : survivorLookBackNoEntryNote(lookBack.name),
        }
      : phase.kind === "noContest" && upcomingNote !== null
        ? { kind: "noContest", note: upcomingNote }
        : phase;

  const view: typeSurvivorGameView =
    contestList.isLoading ||
    contest.isLoading ||
    entries.isLoading ||
    board.isLoading
      ? { kind: "loading" }
      : contestList.isError || contest.isError || board.isError
        ? { kind: "failed" }
        : shownPhase;

  function refetchBoard(): void {
    void board.refetch();
  }

  function retry(): void {
    void contestList.refetch();
    void contest.refetch();
    void roundList.refetch();
    void board.refetch();
  }

  return {
    contestId,
    contestOptions,
    contestMenuValue: lookBack?.contestId ?? viewerContestId ?? undefined,
    isLookingBack: lookBack !== null,
    contestData,
    canAddEntry,
    entryOptions,
    selectedEntryId,
    entryStatus,
    hasEntry,
    roundChips,
    selectedRoundId,
    boardData,
    view,
    hasScheduledContest,
    teamsById: teamNames.teamsById,
    refetchBoard,
    retry,
  };
}
